import { SEMANTIC_SUCCESS, BRAND_PRIMARY } from '@virtuetasks/types';
/**
 * Chart primitives — Recharts, per spec §2.1.
 *
 * Wrapped rather than used raw so every chart reads the same design tokens and
 * responds to the theme. Recharts takes literal colours, not CSS variables, so
 * the current computed values are read once and re-read when the theme changes.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export const PRIORITY_COLOURS: Record<string, string> = {
  P0: '#C2413A',
  P1: '#C9762B',
  P2: '#4A7CB5',
  P3: '#7C838A',
};

export const CATEGORY_COLOURS: Record<string, string> = {
  todo: '#7C838A',
  in_progress: '#C9762B',
  done: SEMANTIC_SUCCESS,
  cancelled: '#9BA1A8',
};

/** Reads the live token values so charts follow the light/dark switch. */
export function useChartTheme(): { text: string; muted: string; grid: string; accent: string } {
  const read = (): { text: string; muted: string; grid: string; accent: string } => {
    const style = getComputedStyle(document.documentElement);
    return {
      text: style.getPropertyValue('--text').trim() || '#16181A',
      muted: style.getPropertyValue('--text-muted').trim() || '#6B7076',
      grid: style.getPropertyValue('--border').trim() || '#E4E4E2',
      // V8-03 §1 — the *brand* role, so the fallback follows the brand.
      accent: style.getPropertyValue('--accent').trim() || BRAND_PRIMARY,
    };
  };

  const [theme, setTheme] = useState(read);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(read()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return theme;
}

export function ChartCard({
  title,
  subtitle,
  children,
  actions,
  testId,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
  testId?: string;
}) {
  return (
    <section className="vt-surface p-3" data-testid={testId}>
      <header className="mb-2 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-[13px] font-medium">{title}</h3>
          {subtitle && <p className="text-[11px] vt-muted">{subtitle}</p>}
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone,
  testId,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'default' | 'danger' | 'warn';
  testId?: string;
}) {
  const colour =
    tone === 'danger' ? 'var(--danger)' : tone === 'warn' ? 'var(--warn)' : 'var(--text)';
  return (
    <div className="vt-surface p-3" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide vt-muted">{label}</div>
      <div className="vt-mono text-[28px] font-semibold leading-tight" style={{ color: colour }}>
        {value}
      </div>
      {hint && <div className="text-[11px] vt-muted">{hint}</div>}
    </div>
  );
}

const AXIS = { fontSize: 11 };

export function SimpleBarChart({
  data,
  xKey,
  yKey,
  height = 200,
  colour,
  cellColour,
}: {
  data: Record<string, unknown>[];
  xKey: string;
  yKey: string;
  height?: number;
  colour?: string;
  cellColour?: (row: Record<string, unknown>) => string;
}) {
  const theme = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
        <CartesianGrid stroke={theme.grid} vertical={false} />
        <XAxis dataKey={xKey} tick={{ ...AXIS, fill: theme.muted }} stroke={theme.grid} />
        <YAxis tick={{ ...AXIS, fill: theme.muted }} stroke={theme.grid} allowDecimals={false} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${theme.grid}` }}
        />
        <Bar dataKey={yKey} fill={colour ?? theme.accent} radius={[3, 3, 0, 0]}>
          {cellColour
            ? data.map((row, i) => <Cell key={i} fill={cellColour(row)} />)
            : null}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function SimpleLineChart({
  data,
  xKey,
  series,
  height = 200,
}: {
  data: Record<string, unknown>[];
  xKey: string;
  series: { key: string; label: string; colour?: string }[];
  height?: number;
}) {
  const theme = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
        <CartesianGrid stroke={theme.grid} vertical={false} />
        <XAxis dataKey={xKey} tick={{ ...AXIS, fill: theme.muted }} stroke={theme.grid} />
        <YAxis tick={{ ...AXIS, fill: theme.muted }} stroke={theme.grid} allowDecimals={false} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${theme.grid}` }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.colour ?? theme.accent}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function BurnUpChart({
  data,
  height = 220,
}: {
  data: { date: string; scope: number; completed: number }[];
  height?: number;
}) {
  const theme = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
        <CartesianGrid stroke={theme.grid} vertical={false} />
        <XAxis dataKey="date" tick={{ ...AXIS, fill: theme.muted }} stroke={theme.grid} />
        <YAxis tick={{ ...AXIS, fill: theme.muted }} stroke={theme.grid} allowDecimals={false} />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${theme.grid}` }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Area
          type="monotone"
          dataKey="scope"
          name="Scope"
          stroke={theme.muted}
          fill={theme.muted}
          fillOpacity={0.12}
        />
        <Area
          type="monotone"
          dataKey="completed"
          name="Completed"
          stroke={theme.accent}
          fill={theme.accent}
          fillOpacity={0.25}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function StatusDonut({
  data,
  height = 200,
}: {
  data: { name: string; category: string; count: number }[];
  height?: number;
}) {
  const theme = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} dataKey="count" nameKey="name" innerRadius="55%" outerRadius="85%">
          {data.map((row, i) => (
            <Cell key={i} fill={CATEGORY_COLOURS[row.category] ?? theme.muted} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, border: `1px solid ${theme.grid}` }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}
